/**
 * analytics.ts
 *
 * StoryBlock-specific analytics helpers. Exposes small, focused functions
 * used by StoryBlock event handlers to emit consistent CleverTap/dataLayer
 * payloads. Keeping this file separate isolates StoryBlock CT shape logic
 * from other live-commerce helpers and makes testing easier.
 */
/**
 * analytics.ts (StoryBlock)
 *
 * Thin wrapper utilities used by `StoryBlock` event handlers to build
 * and emit analytics payloads. This module centralizes CleverTap and
 * dataLayer shaping so UI code stays small and tests can assert
 * payload shapes easily.
 *
 * Exports:
 * - `trackEnrichedClick(args)`: builds/enriches a CleverTap payload and
 *   sends via the shared `dataLayer` helper.
 * - `trackRawView(args)`: emits impression/view payloads used on
 *   StoryBlock content-ready and generated events.
 *
 * Consumers: `apps/bfl-live-commerce-app/libs/src/utils/storyblock/storyBlockEventHandlers.ts`
 */

import {
  dataLayer,
  type DataLayerProps,
  type CombinedClevertapProps,
} from "@repo/analytics/data-layer-utils";
import { dlElementConstants } from "@repo/analytics/data-layer.constants";
import { enrichStoryBlockCT } from "../../utils/livecommerceCleverTap";
import type { VideoDetails } from "./storyBlockTypes";

type EnrichedClickArgs = {
  EVENT_NAME: string;
  EP_PRODUCT_TITLE?: string;
  EP_PRODUCT_ID?: string;
  origin?: string;
  title: string;
  titleCt?: string;
  videoDetails?: VideoDetails;
  customerId?: string;
  eventType?: string;
  eventTypeValue?: "click" | "view";
  extra?: Record<string, unknown>;
};

/**
 * Track a click/action originating from StoryBlock.
 *
 * Builds an enriched CleverTap object via `enrichStoryBlockCT` and sends
 * it to the shared `dataLayer` helper.
 *
 * @param args - contextual fields describing the click/action
 */
export function trackEnrichedClick(args: EnrichedClickArgs) {
  const {
    EVENT_NAME,
    EP_PRODUCT_TITLE,
    EP_PRODUCT_ID,
    origin,
    title,
    titleCt,
    videoDetails,
    customerId,
    eventType,
    eventTypeValue,
    extra,
  } = args;

  // Derive EP_CTA from eventType when not explicitly provided
  const eventToCtaMap: Record<string, string> = {
    "fw:player:quit": "QUIT",
    "fw:player:transition": "PIP_TRANSITION",
  };

  const derivedCta = eventType ? eventToCtaMap[eventType] : undefined;

  const base = enrichStoryBlockCT(
    {
      EVENT_NAME,
      ...(EP_PRODUCT_TITLE ? { EP_PRODUCT_TITLE } : {}),
      ...(EP_PRODUCT_ID ? { EP_PRODUCT_ID } : {}),
      ...(derivedCta ? { EP_CTA: derivedCta } : {}),
    },
    {
      origin,
      title,
      titleCt,
      videoDetails,
      customerId,
      eventType,
      eventTypeValue,
    }
  );

  const payload = {
    ...base,
    ...extra,
  };
  dataLayer("", {} as DataLayerProps, payload as unknown as CombinedClevertapProps);
}

type RawViewArgs = {
  origin?: string;
  title: string;
  titleCt?: string;
  customerId?: string;
  currentVideoId?: string;
  currentVideoTitle?: string;
  currentLiveStreamStatus?: string;
  videoDetails?: VideoDetails;
};

/**
 * Track a raw impression/view for StoryBlock. Maps influencer origin to
 * the appropriate event names and populates standard EP_* fields.
 *
 * @param args - view context fields
 */
export function trackRawView(args: RawViewArgs) {
  const {
    origin,
    customerId,
    currentVideoId,
    currentVideoTitle,
    currentLiveStreamStatus,
    videoDetails,
  } = args;

  const obj: Record<string, unknown> = {
    EVENT_NAME:
      origin === "influencers"
        ? "SOCIAL_COMMERCE_VIEWED"
        : dlElementConstants.LIVE_COMMERCE_PAGE_VIEWED,
    EP_PAGE_NAME: origin === "influencers" ? "INFLUENCER_PAGE" : "LIVE LANDING PAGE CLP",
    EP_PAGE_URL: typeof window !== "undefined" ? window.location.href : "NA",
    EP_LIVEVIDEO_IMPRESSION: currentVideoId || "NA",
    EP_ID: currentVideoId || "NA",
    EP_VIDEO_TITLE: currentVideoTitle || (videoDetails?.video?.caption ?? "NA"),
    EP_STATUS:
      (videoDetails?.video?.live_stream_status as string) || currentLiveStreamStatus || "NA",
    EP_LIVE_SESSION:
      (videoDetails?.video?.live_stream_status as string) === "active" ? "ACTIVE" : "INACTIVE",
    EP_EVENT_TYPE: "VIEW",
    EP_JOURNEY: origin === "influencers" ? "INFLUENCER_PAGE" : "LIVE_COMMERCE",
    EP_SEGMENT: origin === "influencers" ? "SOCIAL_COMMERCE" : "LIVE_COMMERCE",
    EP_CUSTOMER_ID: customerId || "NA",
  };
  dataLayer("", {} as DataLayerProps, obj as CombinedClevertapProps);
}
